// Szövegek külön véve a kódtól (4.1).
//
// A webes app szövegei eddig be voltak égetve magyarul, a Flutter oldalon
// viszont már megvan a `lib/i18n/hu.json` / `en.json` pár, 901 kulccsal. Ez a
// modul UGYANAZT a kulcsszerkezetet használja (`auth.loginTitle`,
// `task.deadline`), hogy a két felület egy szótárból dolgozzon, és egy
// fordítás ne kétszer készüljön el.
//
// Két tudatos eltérés a Flutter `localization` csomagtól:
//
//  1. HIÁNYZÓ KULCSNÁL a teljes kulcsot adjuk vissza, nem az első szakaszát.
//     A Flutter viselkedése (`'auth.wrongPassword'.i18n()` → `'auth'`, ha a
//     szótár nincs betöltve) csendes: a képernyőn megjelenik egy „auth" szó,
//     és minden kulcs ugyanazt adja. Ez a mobil teszteket is megtévesztette.
//     A teljes kulcs viszont azonnal látszik a felületen, és greppelhető.
//
//  2. A betöltés ELŐTT nem lehet fordítani: a `t()` ilyenkor is a kulcsot
//     adja. A `main.ts` a `loadDictionary()`-t a render ELŐTT várja meg.

export type Dictionary = Record<string, string>;

export type Language = 'hu' | 'en';

let dictionary: Dictionary = {};
let current: Language = 'hu';

/// A beágyazott szótárat lapos kulcsokká alakítja: `{auth: {login: 'x'}}` →
/// `{'auth.login': 'x'}`. A Flutter fájlok beágyazottak, a keresés viszont
/// lapos kulccsal a leggyorsabb.
export function flatten(source: unknown, prefix = ''): Dictionary {
  const out: Dictionary = {};
  if (typeof source !== 'object' || source === null) return out;

  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      out[path] = value;
    } else if (typeof value === 'object' && value !== null) {
      Object.assign(out, flatten(value, path));
    }
  }
  return out;
}

/// Behelyettesítés a Flutter `.i18n([...])` mintájára: a szövegben `%s`
/// helyőrzők állnak, sorrendben.
///
/// A `%1$s` alak is használható, és ez NEM díszítés: a szórend nyelvenként
/// más. A dátum magyarul „2026. szeptember 30", angolul „September 30, 2026"
/// — ugyanabból a három adatból, MÁS sorrendben. Sorrendhez kötött `%s`-sel
/// az angol fordítás egyszerűen nem lenne leírható.
export function interpolate(text: string, args?: readonly string[]): string {
  if (!args || args.length === 0) return text;
  let index = 0;
  return text.replace(/%(\d+)\$s|%s/g, (match, position?: string) => {
    if (position !== undefined) return args[Number(position) - 1] ?? match;
    return args[index++] ?? match;
  });
}

/// Egy szöveg a kulcsa alapján.
///
/// Hiányzó kulcsnál MAGÁT A KULCSOT adja vissza — így a hiány a képernyőn is
/// látszik, és nem egy félrevezető szórészlet jelenik meg helyette.
export function t(key: string, args?: readonly string[]): string {
  const text = dictionary[key];
  if (text === undefined) return key;
  return interpolate(text, args);
}

/// Van-e fordítás erre a kulcsra. A migráció közben hasznos: egy képernyő
/// átírható úgy, hogy hiányzó kulcsnál a régi, beégetett szöveg maradjon.
export function has(key: string): boolean {
  return dictionary[key] !== undefined;
}

export function language(): Language {
  return current;
}

/// A látogató nyelvválasztása — a böngésző tárolójában.
const NYELV_KULCS = 'blaze.nyelv';

/// Melyik nyelven induljon az oldal.
///
/// A sorrend szándékos:
///   1. amit a látogató KORÁBBAN választott — az ő döntése felülír mindent,
///   2. a böngésző nyelve, ha az magyar,
///   3. minden máson ANGOL.
///
/// A hármas pont ugyanaz a szabály, mint a mobilon (`app.dart`
/// `localeResolutionCallback`): aki nem magyar, annak az angol többet mond,
/// mint a magyar. Korábban a web MINDENKINEK magyarul indult.
///
/// A tároló elérhetetlen is lehet (privát ablak, letiltott sütik) — ilyenkor
/// nincs mentett döntés, és a böngésző nyelve dönt. Kivételt nem engedünk ki:
/// a nyelvválasztás nem érhet annyit, hogy elhasaljon tőle az indulás.
export function detectLanguage(): Language {
  try {
    const mentett = localStorage.getItem(NYELV_KULCS);
    if (mentett === 'hu' || mentett === 'en') return mentett;
  } catch {
    /* elérhetetlen tároló — megyünk tovább a böngésző nyelvére */
  }

  const nyelvek: readonly string[] =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];

  for (const nyelv of nyelvek) {
    if (typeof nyelv === 'string' && nyelv.toLowerCase().startsWith('hu')) {
      return 'hu';
    }
  }
  return 'en';
}

/// Nyelvváltás: eltároljuk a döntést, és ÚJRATÖLTJÜK a lapot.
///
/// Az újratöltés nem lustaság. A képernyők a `t()` eredményét a felépítéskor
/// olvassák ki, nem kötik hozzá: egy helyben cserélt szótár után a már
/// kirajzolt szövegek a régi nyelven maradnának. Újratöltéssel minden
/// komponens egyszer, a helyes nyelven épül fel.
export function setLanguage(lang: Language): void {
  if (lang === current) return;
  try {
    localStorage.setItem(NYELV_KULCS, lang);
  } catch {
    /* A választás nem marad meg, de a mostani oldalbetöltésre érvényes. */
  }
  window.location.reload();
}

/// A szótár betöltése. A `main.ts` a render ELŐTT várja meg.
///
/// Dinamikus importtal: a nem használt nyelv így ki sem kerül a fő csomagba.
export async function loadDictionary(lang: Language = 'hu'): Promise<void> {
  const source =
    lang === 'en'
      ? (await import('../i18n/en.json')).default
      : (await import('../i18n/hu.json')).default;

  dictionary = flatten(source);
  current = lang;
  document.documentElement.lang = lang;
}

/// Csak a teszteknek és a fejlesztői előnézetnek: szótár beállítása betöltés
/// nélkül.
export function setDictionary(source: unknown, lang: Language = 'hu'): void {
  dictionary = flatten(source);
  current = lang;
}
